import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { matchesAPI, teamsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function MatchListPage() {
  const navigate = useNavigate();
  const { currentTeam } = useAuth();
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch teams on mount
  useEffect(() => {
    const loadTeams = async () => {
      try {
        const data = await teamsAPI.getMyTeams();
        setTeams(data);
        
        if (currentTeam) {
          setSelectedTeamId(currentTeam.id.toString());
        }
      } catch (error) {
        console.error('Failed to load teams:', error);
      }
    };
    loadTeams();
  }, [currentTeam]);

  const fetchMatches = useCallback(async () => {
    try {
      const teamId = selectedTeamId ? parseInt(selectedTeamId) : null;
      const data = await matchesAPI.getMatches(teamId);
      setMatches(data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedTeamId]);

  // Fetch matches when team changes
  useEffect(() => {
    fetchMatches();
    const interval = setInterval(fetchMatches, 5000);
    return () => clearInterval(interval);
  }, [fetchMatches]);

  const handleDelete = async (matchId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this match?')) {
      try {
        await matchesAPI.deleteMatch(matchId);
        fetchMatches();
      } catch (error) {
        console.error('Failed to delete match:', error);
      }
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      uploaded: { bg: '#dbeafe', color: '#1e40af', text: 'Uploaded' },
      downloading: { bg: '#dbeafe', color: '#1e40af', text: 'Downloading' },
      processing: { bg: '#fef3c7', color: '#92400e', text: 'Processing...' },
      review: { bg: '#d1fae5', color: '#065f46', text: 'Ready for Review' },
      completed: { bg: '#d1fae5', color: '#065f46', text: 'Completed' },
      error: { bg: '#fee2e2', color: '#991b1b', text: 'Error' }
    };
    const style = styles[status] || styles.uploaded;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        background: style.bg,
        color: style.color
      }}>
        {style.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p>Loading matches...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Match Videos</h1>
          <p style={styles.subtitle}>
            {matches.length} match{matches.length !== 1 ? 'es' : ''} found
          </p>
        </div>
        <button 
          onClick={() => navigate('/upload')}
          style={styles.uploadButton}
        >
          + Upload Video
        </button>
      </div>

      {/* Team Filter */}
      {teams.length > 1 && (
        <div style={styles.filterBar}>
          <select
            value={selectedTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
            style={styles.select}
          >
            <option value="">All Teams</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Matches Grid */}
      {matches.length === 0 ? (
        <div style={styles.emptyState}>
          <span style={{ fontSize: '64px' }}>🎥</span>
          <h2>No matches yet</h2>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            Upload your first match video to start analyzing
          </p>
          <button 
            onClick={() => navigate('/upload')}
            style={styles.uploadButton}
          >
            Upload Video
          </button>
        </div>
      ) : (
        <div style={styles.grid}>
          {matches.map(match => (
            <div key={match.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div>
                  <h3 style={styles.matchTitle}>{match.title}</h3>
                  <p style={styles.matchDate}>{formatDate(match.created_at)}</p>
                </div>
                {getStatusBadge(match.status)}
              </div>

              {(match.home_team || match.away_team) && (
                <div style={styles.teamsRow}>
                  <span>{match.home_team || 'TBD'}</span>
                  <span style={{ color: '#9ca3af' }}>vs</span>
                  <span>{match.away_team || 'TBD'}</span>
                  {match.final_score && (
                    <span style={styles.score}>{match.final_score}</span>
                  )}
                </div>
              )}

              <div style={styles.statsRow}>
                {match.duration > 0 && (
                  <div style={styles.stat}>
                    <span style={styles.statIcon}>⏱️</span>
                    <span>{formatDuration(match.duration)}</span>
                  </div>
                )}
                {match.events_count > 0 && (
                  <div style={styles.stat}>
                    <span style={styles.statIcon}>🎯</span>
                    <span>{match.events_count} events</span>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {match.status === 'processing' && (
                <div style={styles.progressContainer}>
                  <div style={styles.progressHeader}>
                    <span>Processing video...</span>
                    <span style={{ fontWeight: '600', color: '#667eea' }}>
                      {(match.progress || 0).toFixed(0)}%
                    </span>
                  </div>
                  <div style={styles.progressBar}>
                    <div 
                      style={{
                        ...styles.progressFill,
                        width: `${match.progress || 0}%`
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={styles.actions}>
                {match.status === 'review' || match.status === 'completed' ? (
                  <>
                    <Link 
                      to={`/matches/${match.id}/review`}
                      style={styles.primaryButton}
                    >
                      Review Events
                    </Link>
                    <Link 
                      to={`/matches/${match.id}/stats`}
                      style={styles.secondaryButton}
                    >
                      Stats
                    </Link>
                  </>
                ) : match.status === 'processing' ? (
                  <button style={styles.disabledButton} disabled>
                    ⏳ Processing...
                  </button>
                ) : match.status === 'error' ? (
                  <button 
                    style={styles.retryButton}
                    onClick={() => matchesAPI.processMatch(match.id).then(fetchMatches)}
                  >
                    🔄 Retry
                  </button>
                ) : (
                  <button style={styles.disabledButton} disabled>
                    Waiting...
                  </button>
                )}
                
                <button 
                  onClick={(e) => handleDelete(match.id, e)}
                  style={styles.deleteButton}
                  title="Delete match"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Auto-refresh indicator */}
      {matches.length > 0 && (
        <div style={styles.refreshNote}>
          <span>🔄</span> Auto-refreshing every 5 seconds
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px 20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1e3a8a',
    margin: 0,
  },
  subtitle: {
    color: '#6b7280',
    marginTop: '4px',
  },
  filterBar: {
    marginBottom: '24px',
  },
  select: {
    padding: '10px 16px',
    fontSize: '14px',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    minWidth: '200px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  matchTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937',
    margin: 0,
  },
  matchDate: {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '4px',
  },
  teamsRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    fontSize: '15px',
    color: '#374151',
    marginBottom: '16px',
  },
  score: {
    marginLeft: 'auto',
    fontWeight: '700',
    color: '#1e40af',
  },
  statsRow: {
    display: 'flex',
    gap: '20px',
    marginBottom: '16px',
  },
  stat: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    color: '#6b7280',
  },
  statIcon: {
    fontSize: '16px',
  },
  progressContainer: {
    marginBottom: '16px',
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#6b7280',
    marginBottom: '8px',
  },
  progressBar: {
    height: '6px',
    background: '#e5e7eb',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #667eea, #764ba2)',
    transition: 'width 0.5s ease',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  },
  primaryButton: {
    flex: 1,
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'none',
    textAlign: 'center',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '10px 16px',
    background: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'none',
    textAlign: 'center',
    cursor: 'pointer',
  },
  disabledButton: {
    flex: 1,
    padding: '10px 16px',
    background: '#e5e7eb',
    color: '#9ca3af',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'not-allowed',
  },
  retryButton: {
    flex: 1,
    padding: '10px 16px',
    background: '#fef3c7',
    color: '#92400e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '10px 12px',
    background: '#fee2e2',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
  },
  uploadButton: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 24px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  refreshNote: {
    textAlign: 'center',
    marginTop: '24px',
    fontSize: '13px',
    color: '#9ca3af',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    color: '#6b7280',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e5e7eb',
    borderTopColor: '#667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
};

export default MatchListPage;
